import { useFilterAndSearchEffect } from "./useFilterAndSearchEffect";
import { useFilterPayload } from "./useFilterPayload";
import { MedicalPaOrdersState } from "./useMedicalPaOrdersTableData";
import { usePaginationReset } from "./usePaginationReset";
import { useSearchTerm } from "./useSearchTerm";
import { useValidFilters } from "./useValidFilters";

/**
 * Orchestrates filter and search state for the medical PA orders table.
 * Composes useSearchTerm, usePaginationReset, useValidFilters, and useFilterPayload
 * to produce a unified filter/search API and triggers data re-fetching via useFilterAndSearchEffect.
 *
 * @param initialDataFetchingExecuted - Whether the initial data load has completed
 * @param setTableData - State setter for table row data
 * @param setMedicalPaOrdersState - State setter for the orders loading/data lifecycle
 * @returns Filter payload, search term controls, pagination reset counter, and active filters
 */
export const useFilterAndSearch = (
  initialDataFetchingExecuted: boolean,
  setTableData: React.Dispatch<React.SetStateAction<any[]>>,
  setMedicalPaOrdersState: React.Dispatch<
    React.SetStateAction<MedicalPaOrdersState>
  >,
) => {
  const { searchTerm, setSearchTerm } = useSearchTerm();
  const { paginationResetCount, setPaginationResetCount } =
    usePaginationReset();
  const validFilters = useValidFilters();
  const { filterPayload } = useFilterPayload(
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
  );

  useFilterAndSearchEffect(
    filterPayload,
    initialDataFetchingExecuted,
    setTableData,
    setMedicalPaOrdersState,
    setPaginationResetCount,
  );

  return {
    filterPayload,
    searchTerm,
    setSearchTerm,
    paginationResetCount,
    setPaginationResetCount,
    validFilters,
  };
};
